import { ColorOptions } from '@jfs/ui-new'
import { isEmptyObject, padZero } from '@jfs/utils'
import Base64 from 'crypto-js/enc-base64'
import Utf8 from 'crypto-js/enc-utf8'
import { JSEncrypt } from 'jsencrypt'
import { get } from 'lodash'
import { lazy } from 'react'
import { Dimensions, Platform } from 'react-native'
import * as Yup from 'yup'
import { MIN_HEIGHT_FOR_NO_ZOOM, ZOOM_FRACTION } from './constants'
import { SearchEntityHierachy } from '../screens/EntityManagement/SearchEntityHierachy'

const CheckerDetails = lazy(() => import('../screens/EntityOnboarding/CheckerDetails'))

const MyOnboardedEntityQueue = lazy(() => import('../screens/EntityOnboarding/MyOnboardedEntityQueue'))
const MyOnboardedEntityDetails = lazy(() => import('../screens/EntityOnboarding/MyOnboardedEntityDetails'))
const OnboardEntity = lazy(() => import('../screens/EntityOnboarding/OnboardEntity'))

const CheckerQueue = lazy(() => import('../screens/EntityOnboarding/CheckerQueue'))
const OnBoardingIncidents = lazy(() => import('../screens/EntityOnboarding/Incidents'))
const IncidentDetails = lazy(() => import('../screens/EntityOnboarding/Incidents/IncidentDetails'))

const BulkMerchantPayoutConfig = lazy(() => import('../screens/BulkMerchantPayoutConfig'))
const LedgerMappingDetails = lazy(() => import('../screens/LedgerMapping/LedgerMappingDetails'))
const BulkLedgerMapping = lazy(() => import('../screens/BulkLedgerMapping/'))
const AddUpdateUser = lazy(() => import('../screens/AddUpdateUser/'))
const AddUpdateGroup = lazy(() => import('../screens/AddUpdateGroup/'))
const BankingCodes = lazy(() => import('../screens/BankingCodes/'))
const EditBankingCode = lazy(() => import('../screens/BankingCodes/EditBankingCode'))
const Devices = lazy(() => import('../screens/Devices/'))
const EMSDevices = lazy(() => import('../screens/EMSDevices/'))
const EntityApprovals = lazy(() => import('../screens/EntityApprovals/'))
const EntityMaster = lazy(() => import('../screens/EntityMaster/'))
const EditEntity = lazy(() => import('../screens/EntityMaster/EditEntity'))
const FileUpload = lazy(() => import('../screens/FileUpload/'))
const GenerateExcelFile = lazy(() => import('../screens/GenerateExcelFile/'))
const ExcelFileApprovals = lazy(() => import('../screens/GenerateExcelFile/ExcelFileApprovals'))
const Home = lazy(() => import('../screens/Home/'))
const MerchantMapping = lazy(() => import('../screens/MerchantMapping/'))
const MerchantDetails = lazy(() => import('../screens/MerchantMapping/MerchantDetails'))
const FinHubReports = lazy(() => import('../screens/FinHubReports/'))
const ViewAndAddDeviceVariant = lazy(() => import('../screens/ViewAndAddDeviceVariant/'))
const ViewAndAddDevicePlans = lazy(() => import('../screens/ViewAndAddDevicePlans'))
const OnboardDevices = lazy(() => import('../screens/OnboardDevices'))
const ViewAssignedDevices = lazy(() => import('../screens/ViewAssignedDevices'))
const EntityConfigurations = lazy(() => import('../screens/EntityConfigurations/'))

const Package = lazy(() => import('../screens/Package/'))
const PackageToInstrument = lazy(() => import('../screens/PackageToInstrument/'))
const PackageToMId = lazy(() => import('../screens/PackageToMId/'))
const DynamicRouting = lazy(() => import('../screens/DynamicRouting/'))
const RoutingApprovals = lazy(() => import('../screens/RoutingApprovals/'))
const RoutingRules = lazy(() => import('../screens/RoutingRules/'))
const RuleDetails = lazy(() => import('../screens/RoutingRules/RuleDetails'))
const SystemControlConfigApprovals = lazy(() => import('../screens/SystemControlConfigApprovals/'))
const Users = lazy(() => import('../screens/Users/'))
const Details = lazy(() => import('../screens/Users/Details'))
const BulkMerchantOnboarding = lazy(() => import('../screens/BulkMerchantOnboarding/'))
const SapOnboarding = lazy(() => import('../screens/SapOnboarding/'))
const EntityManagement = lazy(() => import('../screens/EntityManagement'))
const CreateGroup = lazy(() => import('../screens/EntityManagement/CreateGroup'))
const CreateSubGroup = lazy(() => import('../screens/EntityManagement/CreateSubGroup'))
const CreateSingleEntity = lazy(() => import('../screens/EntityManagement/CreateSingleEntity'))
const CreateBulkEntities = lazy(() => import('../screens/EntityManagement/CreateBulkEntities'))
const CreateTID = lazy(() => import('../screens/EntityManagement/CreateTID'))
const SearchEntity = lazy(() => import('../screens/EntityManagement/SearchEntity'))
const GlobalSearchEntityList = lazy(() => import('../screens/EntityOnboarding/GlobalSearchList'))
const SearchEntityDetails = lazy(() => import('../screens/EntityOnboarding/SearchEntityDetails'))
const Checker = lazy(() => import('../screens/Checker'))
const CheckerMyQueue = lazy(() => import('../screens/Checker/MyQueue'))
const CheckStatus = lazy(() => import('../screens/Checker/Status'))
const MakerQueue = lazy(() => import('../screens/Maker'))
const BulkMerchantMapping = lazy(() => import('../screens/BulkMerchantMapping/'))
const EssControlUpdate = lazy(() => import('../screens/EssControlUpdate'))
const GenericMakerCheckerScreen = lazy(() => import('../screens/GenericMakerCheckerScreen'))
const MerchantConfig = lazy(() => import('../screens/MerchantConfig/'))
const LedgerMapping = lazy(() => import('../screens/LedgerMapping/'))
const PayoutConfig = lazy(() => import('../screens/PayoutConfig/'))
const SourceAccountConfig = lazy(() => import('../screens/SourceAccountConfig/'))
const HoldTransaction = lazy(() => import('../screens/RiskConfig/HoldTransaction'))
const PayoutCmsUpdate = lazy(() => import('../screens/PayoutCmsUpdate'))
const BulkPayoutCmsUpdate = lazy(() => import('../screens/BulkPayoutCmsUpdate'))
const FeePackage = lazy(() => import('../screens/FeePackage/'))
const FeePackageFileUpload = lazy(() => import('../screens/FeePackageFileUpload/'))
const FeePackageToMId = lazy(() => import('../screens/FeePackageToMId/'))
const FeePackageApprovals = lazy(() => import('../screens/FeePackageApprovals/'))
const MerchantPayoutConfigApprovals = lazy(() => import('../screens/MerchantPayoutConfigApprovals/'))
const ProcessingEntity = lazy(() => import('../screens/ProcessingEntity/'))
const ProcessingEntityDetails = lazy(() => import('../screens/ProcessingEntity/ProcessingEntityDetails'))
const OnHoldSettlements = lazy(() => import('../screens/OnHoldSettlements'))
const CreditAdjustment = lazy(() => import('../screens/CreditAdjustment'))
const EmsApprovals = lazy(() => import('../screens/EmsApprovals'))
const ApprovedPos = lazy(() => import('../screens/ApprovedPos/'))
const PosApprovals = lazy(() => import('../screens/PosApprovals/'))
const CreateBulkConfig = lazy(() => import('../screens/EntityManagement/CreateBulkConfig'))
const CreateBulkCards = lazy(() => import('../screens/EntityManagement/CreateBulkCards'))
const CreateBulkLovs = lazy(() => import('../screens/EntityManagement/CreateBulkLovs'))
const FiuCompliance = lazy(() => import('../screens/EntityManagement/FiuCompliance'))
const FiuQueue = lazy(() => import('../screens/EntityManagement/FiuQueue'))
const FiuMakerQueue = lazy(() => import('../screens/EntityManagement/FiuMaker'))
const FiuDetails = lazy(() => import('../screens/EntityManagement/FiuDetails'))
const AadharRedaction = lazy(() => import('../screens/EntityManagement/AadharRedaction'))
const AadharCheckerQueue = lazy(() => import('../screens/EntityManagement/AadharRedaction/Checker'))
const AadharMakerQueue = lazy(() => import('../screens/EntityManagement/AadharRedaction/Maker'))
const AadharRedactionDetails = lazy(() => import('../screens/EntityManagement/AadharRedaction/Details'))
const CreateBulkVpa = lazy(() => import('../screens/EntityManagement/CreateBulkVpa'))
const CreateBulkStore = lazy(() => import('../screens/EntityManagement/CreateBulkStore'))
const CreateBulkTerminal = lazy(() => import('../screens/EntityManagement/CreateBulkTerminal'))
const CreateBulkUsers = lazy(() => import('../screens/EntityManagement/CreateBulkUsers'))
const ManageSQRs = lazy(() => import('../screens/ManageSQRs'))
const BulkEssUpdate = lazy(() => import('../screens/BulkEssUpdate'))
const BulkMerchantPayoutConfigApprovals = lazy(() => import('../screens/BulkMerchantPayoutConfigApprovals'))
const BulkActionTabScreen = lazy(() => import('../screens/FraudAndRiskManagement/BulkActionTabScreen'))
const AuditTabScreen = lazy(() => import('../screens/FraudAndRiskManagement/AuditTabScreen'))
const PostRecon = lazy(() => import('../screens/PostRecon'))
export const PAGE_ROWS_COUNT = 10

export const IS_ONLY_ONBOARDING = false
// export const ENABLE_BULK_PROCESSING = true
// export const ENABLE_MAKER_MENU = true
// export const ENABLE_CHECKER_MENU = true
export const ENABLE_UPDATE_VIA_MAKER_CHECKER = false

export interface ScreenList {
  home: 'home'
  entitymaster: 'entitymaster'
  cardmapping: 'cardmapping'
  netbankingMapping: 'netbankingMapping'
  merchantmapping: 'merchantmapping'
  approvals: 'approvals'
  routingrules: 'routingrules'
  routingapprovals: 'routingapprovals'
  users: 'users'
  addusers: 'addusers'
  usersgroup: 'usersgroup'
  addgroup: 'addgroup'
  editEntity: 'editEntity'
  editBankingCode: 'editBankingCode'
  ruleDetails: 'ruleDetails'
  createpackage: 'createpackage'
  packageinstrument: 'packageinstrument'
  packagemid: 'packagemid'
  generateExcelFile: 'generateExcelFile'
  generateExcelFileApproval: 'generateExcelFileApproval'
  userDetails: 'userDetails'
}

// const getCheckerMyQueue = () => {
//   return {
//     title: 'My Queue (Checker)',
//     path: 'My Queue (Checker)',
//     comp: CheckerMyQueue,
//   }
// }

// const getMakerQueue = () => {
//   return {
//     title: 'My Queue (Maker)',
//     path: 'My Queue (Maker)',
//     comp: MakerQueue,
//   }
// }

// const getGlobalQueue = () => {
//   return {
//     title: 'Global Queue',
//     path: 'Global Queue',
//     comp: Checker,
//   }
// }
interface ScreenItems {
  title: string
  path: string
  comp: any
  props: any
}

export const ScreenDetails = (path: keyof ScreenList): ScreenItems => {
  const defaultData = {
    title: 'Dashboard',
    path: '',
    comp: Home,
  }

  const ScreenList: any = {
    home: {
      ...defaultData,
    },
    entitymaster: {
      title: 'Entity Master',
      path: 'Entity Config / Entity Master',
      comp: EntityMaster,
    },
    entityconfigurations: {
      comp: EntityConfigurations,
    },
    merchantmapping: {
      title: 'Merchant Mapping',
      path: 'Merchant Mapping',
      comp: MerchantMapping,
    },
    devices: {
      title: 'Devices',
      path: 'Merchant/Devices',
      comp: Devices,
    },
    emsdevices: {
      title: 'EMS Devices',
      path: 'Merchant/Devices',
      comp: EMSDevices,
    },
    approvals: {
      title: 'Approvals',
      path: 'Entity Config/Approvals',
      comp: EntityApprovals,
    },
    routingrules: {
      title: 'Routing Rules',
      path: 'Routing/Rules',
      comp: RoutingRules,
    },
    routingapprovals: {
      title: 'Routing Approvals',
      path: 'Routing/Approvals',
      comp: RoutingApprovals,
    },
    createpackage: {
      title: 'Package Creation',
      path: 'Routing/Package',
      comp: Package,
    },
    packageinstrument: {
      title: 'Package Creation',
      path: 'Routing/Package',
      comp: PackageToInstrument,
    },
    packagemid: {
      title: 'Package Creation',
      path: 'Routing/Package',
      comp: PackageToMId,
    },
    dynamicrouting: {
      title: 'Dynamic Routing',
      path: 'Routing/Dynamic',
      comp: DynamicRouting,
    },
    users: {
      title: 'List Users',
      path: 'Access Management/Users',
      comp: Users,
    },
    addusers: {
      title: 'Add/Modify User',
      path: 'Access Management/Add Modify User',
      comp: AddUpdateUser,
    },
    userDetails: {
      title: 'User Details',
      path: 'Access Management/ User Details',
      comp: Details,
    },
    // usersgroup: {
    //   title: 'List Groups',
    //   path: 'Access Management/Groups',
    //   comp: UserGroup,
    // },
    addgroup: {
      title: 'Add/Modify Group',
      path: 'Access Management/Add Modify Group',
      comp: AddUpdateGroup,
    },
    editEntity: {
      title: 'Edit Entity',
      path: 'Entity Master/ Modify Entity',
      comp: EditEntity,
    },
    merchantDetails: {
      title: 'Merchant Details',
      path: 'Merchant/ Details',
      comp: MerchantDetails,
    },
    ruleDetails: {
      title: 'Routing Rule Details',
      path: 'Routing/ Rule',
      comp: RuleDetails,
    },
    fileupload: {
      title: 'File Upload',
      path: 'File Upload',
      comp: FileUpload,
    },
    generateExcelFile: {
      title: 'Generate Excel File',
      path: 'Generate Excel File',
      comp: GenerateExcelFile,
    },
    generateExcelFileApproval: {
      title: 'Generate Excel File Approvals',
      path: 'Routing/FileApprovals',
      comp: ExcelFileApprovals,
    },
    bankingcodes: {
      title: 'Banking Codes',
      path: 'Banking Codes',
      comp: BankingCodes,
    },
    editBankingCode: {
      title: 'Edit Banking Code',
      path: 'Banking Codes/ Edit Banking Code',
      comp: EditBankingCode,
    },
    systemconfigapprovals: {
      title: 'System Control Approvals',
      path: 'System Control Approvals',
      comp: SystemControlConfigApprovals,
    },
    /* merchantbulkonboarding: {
      title: 'Merchant Bulk Onboarding',
      path: 'Merchant Bulk Onboarding',
      comp: BulkMerchantOnboarding,
    },
    saponboarding: {
      title: 'SAP Onboarding',
      path: 'SAP Onboarding',
      comp: SapOnboarding,
    },*/
    // merchantonboarding: {
    //   title: 'Merchant Onboarding',
    //   path: 'Merchant Onboarding',
    //   comp: SearchMerchant,
    // },
    entityManagement: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: EntityManagement,
    },
    createEntityGroup: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: CreateGroup,
    },
    searchEntityHierarchy: {
      title: 'Search Entity Hierarchy/TID',
      path: 'Search Entity Hierarchy/TID',
      comp: SearchEntityHierachy,
    },
    onboardEntity: {
      title: 'Entity Onboarding',
      path: 'Entity Onboarding',
      comp: OnboardEntity,
    },
    makerMyEntityQueue: {
      title: 'Maker MyQueue',
      path: 'Maker MyQueue',
      comp: MyOnboardedEntityQueue,
    },
    checker1MyQueue: {
      title: 'Checker1 MyQueue',
      path: 'Checker1 MyQueue',
      comp: MyOnboardedEntityQueue,
    },
    checker2MyQueue: {
      title: 'Checker2 MyQueue',
      path: 'Checker2 MyQueue',
      comp: MyOnboardedEntityQueue,
    },
    riskCheckerMyQueue: {
      title: 'RiskChecker MyQueue',
      path: 'RiskChecker MyQueue',
      comp: MyOnboardedEntityQueue,
    },
    riskMakerMyQueue: {
      title: 'RiskMaker MyQueue',
      path: 'RiskMaker MyQueue',
      comp: MyOnboardedEntityQueue,
    },
    myOnboardedEntityDetails: {
      title: 'Onboarded Entity Details',
      path: 'My Onboarded Entity Details',
      comp: MyOnboardedEntityDetails,
    },
    checker1Queue: {
      title: 'Checker1 Queue',
      path: 'checker1Queue',
      comp: CheckerQueue,
    },
    checker2Queue: {
      title: 'Checker2 Queue',
      path: 'checker2Queue',
      comp: CheckerQueue,
    },
    riskCheckerQueue: {
      title: 'RiskChecker Queue',
      path: 'riskCheckerQueue',
      comp: CheckerQueue,
    },
    riskMakerQueue: {
      title: 'RiskMaker Queue',
      path: 'riskMakerQueue',
      comp: CheckerQueue,
    },
    onboardingIncidents: {
      title: 'Incidents',
      path: 'onboardingIncidents',
      comp: OnBoardingIncidents,
    },
    onboardingIncidentDetails: {
      title: 'Incident Details',
      path: 'onboardingIncidentDetails',
      comp: IncidentDetails,
    },
    createEntitySubGroup: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: CreateSubGroup,
    },
    createSingleEntity: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: CreateSingleEntity,
    },
    createBulkEntities: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: CreateBulkEntities,
    },
    createTID: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: CreateTID,
    },
    searchEntity: {
      title: 'Search',
      path: 'Search',
      comp: SearchEntity,
    },
    globalSearchEntityList: {
      title: 'Global Search',
      path: 'Global Search',
      comp: GlobalSearchEntityList,
    },
    globalSearchEntityDetails: {
      title: 'Search EntityDetails',
      path: 'Search EntityDetails',
      comp: SearchEntityDetails,
    },
    checkStatus: {
      title: 'checkerStatus',
      path: 'Checker Status',
      comp: CheckStatus,
    },
    checkerGlobalQueue: {
      title: 'Global Queue',
      path: 'Global Queue',
      comp: Checker,
    },
    checkerMyQueue: {
      title: 'My Queue (Checker)',
      path: 'My Queue (Checker)',
      comp: CheckerMyQueue,
    },
    makerMyQueue: {
      title: 'My Queue (Maker)',
      path: 'My Queue (Maker)',
      comp: MakerQueue,
    },
    // ...(ENABLE_CHECKER_MENU && { checkerGlobalQueue: getGlobalQueue() }),
    // ...(ENABLE_CHECKER_MENU && { checkerMyQueue: getCheckerMyQueue() }),
    // ...(ENABLE_MAKER_MENU && { makerMyQueue: getMakerQueue() }),
    createbulkmerchantmapping: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'ADD_MERCHANT_MAPPING',
      },
    },
    createbulkkeyexchange: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'BULK_KEY_EXCHANGE_UPLOAD',
      },
    },
    approvebulkmerchantmapping: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'APPROVE_MERCHANT_MAPPING',
      },
    },
    bulkmerchantmapping: {
      title: 'Bulk Merchant Mapping',
      path: 'Bulk Merchant Mapping',
      comp: BulkMerchantMapping,
    },
    merchantMappingApprovals: {
      comp: GenericMakerCheckerScreen,
      props: {
        SCREEN_TYPE: 'MERCHANT_MAPPING_APPROVALS',
      },
    },
    esscontrolupdate: {
      title: 'ESS Control Update',
      path: 'ESS Control Update',
      comp: EssControlUpdate,
    },
    merchantconfig: {
      title: 'Update Config',
      path: 'Update Config',
      comp: MerchantConfig,
    },
    bulkmerchantpayoutconfig: {
      title: 'Bulk Merchant Payout Config Update',
      path: 'Bulk Merchant Payout Config Update',
      comp: BulkMerchantPayoutConfig,
    },
    ledgermapping: {
      title: 'Ledger Mapping',
      path: 'Ledger mapping',
      comp: LedgerMapping,
    },
    ledgerDetails: {
      title: 'Ledger Details',
      path: 'Ledger/ Details',
      comp: LedgerMappingDetails,
    },
    bulkledgermapping: {
      title: 'Bulk Ledger Mapping',
      path: 'Bulk Ledger mapping',
      comp: BulkLedgerMapping,
    },
    payoutconfig: {
      title: 'Payout Frequency Config',
      path: 'Payout Frequency Config',
      comp: PayoutConfig,
    },
    sourceaccountconfig: {
      title: 'Source Account Config',
      path: 'Source Account Config',
      comp: SourceAccountConfig,
    },
    holdtransaction: {
      title: 'Hold Transaction',
      path: 'Hold Transaction',
      comp: HoldTransaction,
    },
    updatepayoutcms: {
      title: 'Payout CMS Update',
      path: 'Payout CMS Update',
      comp: PayoutCmsUpdate,
    },
    updatebulkpayoutcms: {
      title: 'Bulk Payout CMS Update',
      path: 'Bulk Payout CMS Update',
      comp: BulkPayoutCmsUpdate,
    },
    essbulkapprovals: {
      title: 'Bulk ESS Update',
      path: 'Bulk ESS Update',
      comp: BulkEssUpdate,
    },
    feepackageroutingapprovals: {
      title: 'FeePackage Routing Approvals',
      path: 'Fee Package/Approvals',
      comp: RoutingApprovals,
    },
    createfeepackage: {
      title: 'FeePackage Creation',
      path: 'Fee Package/Package',
      comp: FeePackage,
    },
    feepackagemid: {
      title: 'FeePackage Creation',
      path: 'Fee package/Package',
      comp: FeePackageToMId,
    },
    feepackagefileupload: {
      title: 'Fee Package File Upload',
      path: 'Fee package/File Upload',
      comp: FeePackageFileUpload,
    },
    feepackageapprovals: {
      title: 'FeePackage Approvals',
      path: 'FeePackage Approvals',
      comp: FeePackageApprovals,
    },
    processingentity: {
      title: 'Processing Entity',
      path: 'Processing Entity',
      comp: ProcessingEntity,
    },
    creditdebitadjustment: {
      title: 'Credit Debit Adjustment',
      path: 'Credit Debit Adjustment',
      comp: CreditAdjustment,
    },
    emsapprovals: {
      title: 'Approvals',
      path: 'Approvals',
      comp: EmsApprovals,
    },
    processingentitydetails: {
      title: 'Processing Entity Details',
      path: 'Processing Entity/ Details',
      comp: ProcessingEntityDetails,
    },
    merchantpayoutconfigapprovals: {
      title: 'Merchant Payout Config Approvals',
      path: 'Merchant Payout Config Approvals',
      comp: MerchantPayoutConfigApprovals,
    },
    bulkmerchantpayoutconfigapprovals: {
      title: 'Bulk Merchant Payout Config Approvals',
      path: 'Bulk Merchant Payout Config Approvals',
      comp: BulkMerchantPayoutConfigApprovals,
    },
    onholdsettlements: {
      title: 'View Dynamic Hold Settlements',
      path: 'View Dynamic Hold Settlements',
      comp: OnHoldSettlements,
    },
    finHubReports: {
      title: 'Finhub Reports',
      path: 'Finhub Reports',
      comp: FinHubReports,
    },
    viewAndAddDevices: {
      title: 'View/Add Devices',
      path: 'View/Add Device Variant',
      comp: ViewAndAddDeviceVariant,
    },
    viewAndAddDevicePlans: {
      title: 'View/Add DevicePlans',
      path: 'View/Add DevicePlans',
      comp: ViewAndAddDevicePlans,
    },
    onboardDevices: {
      title: 'Onboard Devices',
      path: 'Onboard Devices',
      comp: OnboardDevices,
    },
    viewAssignedDevices: {
      title: 'Assigned Devices',
      path: 'Assigned Devices',
      comp: ViewAssignedDevices,
    },
    posapprovals: {
      title: 'Approve POS',
      path: 'Approve POS',
      comp: PosApprovals,
    },
    viewpos: {
      title: 'View POS',
      path: 'View POS',
      comp: ApprovedPos,
    },
    posBatchClosure: {
      comp: GenericMakerCheckerScreen,
      props: {
        SCREEN_TYPE: 'BATCH_MAKER',
      },
    },
    posBatchClosureApprovals: {
      comp: GenericMakerCheckerScreen,
      props: {
        SCREEN_TYPE: 'BATCH_CHECKER',
      },
    },
    bulkPosBatchClosure: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'BATCH_MAKER',
      },
    },
    bulkPosBatchClosureApprovals: {
      title: 'Bulk BatchClosure Approvals',
      path: 'Bulk BatchClosure Approvals',
      comp: CreateBulkLovs,
      props: {
        jobType: 'BATCH_CHECKER',
      },
    },
    createBulkConfig: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: CreateBulkConfig,
    },
    createBulkCards: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: CreateBulkCards,
    },
    createBulkMCCLov: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'MCC_EXPORT',
      },
    },
    createBulkPincodeLov: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'PINCODE_EXPORT',
      },
    },
    createBulkIfscLov: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'IFSC_EXPORT',
      },
    },
    createBulkProgramCategoryLov: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'PROGRAM_CATEGORY_EXPORT',
      },
    },
    createBulkTerminalUpdate: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'BULK_TERMINAL_UPDATE',
      },
    },
    createBulkStoreUpdate: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'BULK_STORE_UPDATE',
      },
    },
    createBulkDeviceUpdate: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'BULK_DEVICES',
      },
    },
    fiuCompliance: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: FiuCompliance,
    },
    fiuQueue: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: FiuQueue,
    },
    fiuMakerQueue: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: FiuMakerQueue,
    },
    fiuDetails: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: FiuDetails,
    },
    aadharRedaction: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: AadharRedaction,
    },
    aadharCheckerQueue: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: AadharCheckerQueue,
    },
    aadharMakerQueue: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: AadharMakerQueue,
    },
    aadharRedactionDetails: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: AadharRedactionDetails,
    },
    checkerDetails: {
      title: 'Checker Details',
      path: 'checkerDetails',
      comp: CheckerDetails,
    },
    createBulkVpa: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: CreateBulkVpa,
    },
    createBulkStore: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: CreateBulkStore,
    },
    createBulkTerminal: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: CreateBulkTerminal,
    },
    postRecon: {
      title: 'Post Recon',
      path: 'Post Recon',
      comp: PostRecon,
    },
    createBulkUsers: {
      title: 'Entity Management',
      path: 'Entity Management',
      comp: window?._env_?.REACT_APP_CREATE_BULK_USER_USING_BOS === 'true' ? CreateBulkLovs : CreateBulkUsers,
      props: {
        jobType: 'BULK_USER_MANAGEMENT',
      },
    },
    ['pair-unpair-sqr']: {
      title: 'Pair/Unpair SQR',
      path: 'Pair/Unpair SQR',
      comp: ManageSQRs,
    },
    fAndRAction: {
      title: 'Trasactions-Risk Management',
      path: 'Trasactions-Risk Management',
      comp: BulkActionTabScreen,
    },
    fAndRAudit: {
      title: 'Trasactions-Risk Management',
      path: 'Trasactions-Risk Management',
      comp: AuditTabScreen,
    },
    checker1BulkClaim: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'CHECKER_1_BULK_CLAIM',
      },
    },
    checker2BulkClaim: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'CHECKER_2_BULK_CLAIM',
      },
    },
    checker1BulkAction: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'CHECKER_1_BULK_ACTION',
      },
    },
    checker2BulkAction: {
      comp: CreateBulkLovs,
      props: {
        jobType: 'CHECKER_2_BULK_ACTION',
      },
    },
  }

  return { ...defaultData, ...(path ? ScreenList[path] : ScreenList.home) }
}
const passwordValidationSchema = Yup.string().min(8, 'Min 8 characters required').required('Required')

const OTPValidationSchema = Yup.string()
  .required('Required')
  .matches(/^[0-9]{6}$/, 'OTP should be 6 digits long')

export { OTPValidationSchema, passwordValidationSchema }

export const getFileType = (path: string) => {
  const fileType: any = {
    downloadBill: 'BILL',
    downloadPaymentReport: 'PAYMENT',
    downloadRncReport: 'REFUND',
    uploadRefund: 'REFUND',
    uploadBill: 'BILL',
  }
  if (path) {
    return fileType[path]
  } else {
    return 'BILL'
  }
}

export const getErrorMessage = (error: any) => {
  console.log('errorMessage', error)

  let message = 'Oops! Encountered an unexpected error from server.'
  if (error?.data?.errors) {
    error?.data?.errors?.forEach((err: any) => {
      message = err?.reason
    })
  } else if (error?.data?.error) {
    if (error?.data?.error.error_description) {
      message = error?.data?.error_description
    } else if (error?.data.error.message) {
      message = error?.data?.error.message
    }
  } else if (error?.message) {
    message = error.message
  } else if (error?.error) {
    message = error?.error?.error_description
  } else if (error?.errors) {
    error?.errors?.forEach((err: any) => {
      if (err?.details) {
        message = err?.reason
      }
    })
  } else if (error?.data?.meta?.message) {
    message = error?.data?.meta?.message
  }

  return message
}

export const getIdFromMap = (data: any) => {
  const value = data?.split('-') ?? data
  return value?.length ? value[0] : data
}

export const isAccessible = (accessConfig: any, type: string) => {
  return get(accessConfig, [type, 'isAccessible'], false)
}

export const MessageTypes = {
  ERROR: 'NEGATIVE',
  SUCCESS: 'POSITIVE',
  INFO: 'NEUTRAL',
}

// error =>  {"meta":{"status":1,"code":"RES_5003","message":"System Error"}}
export const isSuccessResponse = (response: any) => {
  const { data } = response
  console.log('isSuccessResponse', data)
  try {
    if (data?.meta) {
      throw {
        message: data?.meta?.message,
        code: data?.meta?.code,
      }
    }
    if (data?.status === 'FAILED') {
      if (data?.error) {
        throw {
          message: data?.error?.message,
          code: data?.error?.code,
        }
      }
      const failureMessages = data?.failureMessage?.split('|')
      if (failureMessages?.length > 0) {
        throw {
          message: failureMessages?.length === 1 ? failureMessages?.[0] : failureMessages?.[1],
        }
      }
      throw {
        message: failureMessages?.length > 1 ? failureMessages?.[1] : data?.message ?? data?.errorMessage,
      }
    }
    if (data?.status === 'FAILURE') {
      throw {
        message: data?.error?.message,
      }
    }
    if (data?.errors && data?.errors?.length > 0) {
      if (data?.errors?.[0]?.exception?.emsErrorCodeDetailsDto?.channelDesc) {
        throw {
          message: data?.errors?.[0]?.exception?.emsErrorCodeDetailsDto?.channelDesc,
        }
      }
      throw {
        message: data?.errors?.[0]?.message,
      }
    }

    return data
  } catch (error) {
    console.log('isSuccessResponse catch', { error })
    throw error
  }
}

export const isStatusOK = (data: any) =>
  data?.httpCode === 200 && (data?.status?.toUpperCase() === 'SUCCESS' || data?.status === 'INITIATED')

export const clean = (data: any) => {
  const obj = { ...data }
  for (var propName in obj) {
    if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
      delete obj[propName]
    }
  }
  // delete obj?.type
  console.log({ obj })

  return obj
}

export const cleanWithFlattenObject = (data: any) => {
  const obj = { ...data }
  for (var propName in obj) {
    if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
      delete obj[propName]
    }
  }
  // delete obj?.type
  console.log({ obj })

  return obj
}
export const flattenObject = (data: any, bolleanKeys: string[], flattenedObj = {}) => {
  const keys = Object.keys(data)
  for (const key of keys) {
    if (typeof data[key] === 'object' && !Array.isArray(data[key]) && data[key] !== null && !isEmptyObject(data[key])) {
      // Recursively flatten nested object
      const nestedObj = flattenObject(data[key], bolleanKeys, flattenedObj)
      flattenedObj = { ...flattenedObj, ...nestedObj }
    } else if (data[key] !== null && data[key] !== undefined && data[key] !== '' && typeof data[key] !== 'object') {
      // Assign key-value pair
      const value = bolleanKeys.includes(key) ? (data[key] ? true : false) : data[key]
      if (!flattenedObj[key]) {
        flattenedObj[key] = value + ''
      } else if (key.toLocaleLowerCase() !== 'id') {
        flattenedObj[key + '_1'] = value + ''
      }
    }
  }

  return flattenedObj
}

export const camelCaseToTitleCase = (str) => {
  // Replace all capital letters which are not at the beginning with a space followed by the same letter
  let result = str.replace(/([A-Z])/g, ' $1')
  // Capitalize the first letter of each word
  result = result.replace(/^\w/, (c) => c.toUpperCase())
  return result
}

export function removeEmpty(obj) {
  const newObj = removeEmptyValues(obj)
  return removeEmptyObject(newObj)
}

function removeEmptyValues(obj) {
  const newObj = Object.fromEntries(
    Object.entries(JSON.parse(JSON.stringify(obj)))
      .filter(([_, v]) => v !== null && v !== '' && v !== 'NA')
      .map(([k, v]) => [k, v === Object(v) ? removeEmptyValues(v) : v]),
  )
  return newObj
}

function removeEmptyObject(obj) {
  const newObj = Object.fromEntries(
    Object.entries(JSON.parse(JSON.stringify(obj)))
      .filter(([_, v]) => !isEmpty(v))
      .map(([k, v]) => [k, v === Object(v) ? removeEmptyObject(v) : v]),
  )
  return newObj
}

export function isEmpty(obj) {
  return Object.keys(obj)?.length === 0 && obj?.constructor === Object
}
export const isTokenExpired = () => {
  const sData = localStorage.getItem('sessionData')
  if (isEmptyObject(sData)) {
    const isCitrixIp = localStorage.getItem('isCitrixIp')
    localStorage.clear()
    localStorage.setItem('isCitrixIp', isCitrixIp)
    throw {
      message: 'Session Expired, Re-login',
      code: '999999999',
    }
  }
}

export const convertCamelCaseToString = (text: string) => {
  var result = text.replace(/([A-Z])/g, ' $1')
  return result.charAt(0).toUpperCase() + result.slice(1)
}

export const convertSnakeCaseToCaps = (text: string) => {
  var arr = text.split('_')
  let retStr = ''
  arr.map((text) => {
    retStr = retStr + text.charAt(0).toUpperCase() + text.slice(1) + ' '
  })
  return retStr.trim()
}

export const convertToCaps = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const getFilterBody = (search?: string, filter?: string) => {
  if (!!search && !!filter && filter !== 'all') {
    return {
      [filter]: search,
    }
  }
  return {}
}

export const roundOfToTwoDigits = (value: number): string => {
  if (value < 10) {
    return `0${value}`
  }
  return value?.toString()
}

export const formatDate = (time: number) => {
  if (!time) {
    return 'NA'
  } else {
    const date = new Date(time)
    return `${roundOfToTwoDigits(date.getDate())}/${roundOfToTwoDigits(
      date.getMonth() + 1,
    )}/${date.getFullYear()}, ${roundOfToTwoDigits(date.getHours())}:${roundOfToTwoDigits(
      date.getMinutes(),
    )}:${roundOfToTwoDigits(date.getSeconds())}`
  }
}
const PUBLIC_ENC_KEY = window?._env_?.REACT_APP_PUBLIC_ENC_KEY ?? ''
const PUBLIC_ENC_KEY_ACC_MUMBER = window?._env_?.REACT_APP_PUBLIC_ACC_NUMBER_ENC_KEY ?? ''
export const pkceGenerateRandomString = (length: number) => {
  let text = ''
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
  for (var i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length))
  }
  return text
}

const CryptoJS = require('crypto-js')
const pkceBase64URLEncode = (str: any) => {
  const pkceBase64 = str.toString(CryptoJS.enc.Base64).replace(/[=]/g, '').replace(/\+/g, '-').replace(/\//g, '_')
  return pkceBase64
}

export const generateRandomString = (length: number) => {
  let chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let result = ''
  for (let i = length; i > 0; --i) {
    result += chars[Math.floor(Math.random() * chars.length)]
  }
  return result
}

export const AESEncrypt = (securityKey: string, data: string) => {
  let keyUtf8 = Utf8.parse(securityKey)
  let cipherPin = CryptoJS.AES.encrypt(data, keyUtf8, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  })
  return cipherPin.ciphertext.toString(Base64)
}

export const RSAEncrypt = (key: string) => {
  const encrypt = new JSEncrypt()
  encrypt.setPublicKey(PUBLIC_ENC_KEY)
  return encrypt.encrypt(key)
}

export const getEncryptedPwdAndKey = (pwd: string): any => {
  const randomEncKey = generateRandomString(32)
  const aesEncString = AESEncrypt(randomEncKey, pwd)
  const encryptedKey = RSAEncrypt(randomEncKey)
  return { password: aesEncString, key: encryptedKey }
}

export const pkceCodeChallenge = (verifier: string) => {
  const codeChallenge = pkceBase64URLEncode(CryptoJS.SHA256(verifier))
  return codeChallenge
}

export const getEncryptedAuthCodeAndCodeVerifier = (authCode: string, codeVerifier: string): any => {
  const randomEncKey = generateRandomString(32)
  const aesAuthEncString = AESEncrypt(randomEncKey, authCode)
  const codeVerifierEncString = AESEncrypt(randomEncKey, codeVerifier)
  const encryptedKey = RSAEncrypt(randomEncKey)
  return { codeVerifierEnc: codeVerifierEncString, authCodeEnc: aesAuthEncString, key: encryptedKey }
}

export const downloadQrFile = (resp: any, fileName: string) => {
  let type = resp.headers['Content-Type']

  const blob = new Blob([resp?.data], {
    type,
  })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  document.body.appendChild(a)
  a.href = url
  a.download = `${fileName}.jpg`
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

export const generateRandomPassword = (length: number) => {
  let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$'
  let result = ''
  for (let i = length; i > 0; --i) {
    result += chars[Math.floor(Math.random() * chars.length)]
  }
  return result
}

const one_day = 1000 * 60 * 60 * 24

export const generateVpaOrQid = (isVpa = true, extraString = '', prefix = '') => {
  const present_date = new Date()
  const today = new Date(present_date.getFullYear(), present_date.getMonth(), present_date.getDate())
  const first_day = new Date(present_date.getFullYear(), 0, 1)
  const Result = Math.floor((present_date.getTime() - first_day.getTime()) / one_day)
  let julian_date = String(Number(Result) + 1)
  julian_date = ('000' + julian_date).slice(-3)
  const full_year = present_date.getFullYear().toString()
  julian_date = full_year.slice(-2) + julian_date
  const milliseconds = ('000' + present_date.getMilliseconds()).slice(-3)
  const seconds_today = ('00000' + Math.floor((present_date.getTime() - today.getTime()) / 1000)).slice(-5)
  if (isVpa) {
    return prefix + julian_date + seconds_today + milliseconds + extraString + '@jiopay'
  } else {
    return extraString + julian_date + seconds_today + milliseconds + '000'
  }
}

//Function which will remove the @ char and suffix after that if the user enters the complete domain id as we don't need it
export const modifiedUserName = (username: string) => {
  //check for the @ char
  if (username && username?.includes('@')) {
    //removing the substring and trimming the value to remove the spaces if any
    return username?.trim()?.substring(0, username?.trim().indexOf('@'))
  }
  //return the user name if it is the correct one which does not have the @ char sign.
  return username
}

export const convertHexToRGBA = (hexCode: any, opacity: any) => {
  let hex = hexCode.replace('#', '')

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }

  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  return `rgba(${r},${g},${b},${opacity / 100})`
}

export const getImageLocation = (type: string) => {
  const bankids = new Map([
    ['ICICI', '-130px 0px'],
    ['HDFC', '-160px 0px'],
    ['SBI', '-195px 0px'],
    ['UTI', '-8px 0px'],
    ['KOTAK', '-35px 0px'],
  ])
  return bankids.get(type) ?? -1
}

export const returnFormattedTime = (val: number | string) => {
  const stringifiedNumber = val?.toString()
  if (Number(val) < 10 && stringifiedNumber?.length < 2) {
    return `0${val}`
  } else {
    return val
  }
}

export enum DateFormatTypes {
  YYYYMMDD = 'yyyymmdd',
  TIME_STAMP = 'timestamp',
  DAY_ONLY = 'dayonly',
  DATE_HYPHEN = 'datehyphen',
  DATE_STRING = 'datestring',
  DD_MMM_YYYY = 'DD MMM YYYY',
  DD_MM_YYYY = 'DD MM YYYY',
  MONTH_STRING = 'monthstring',
  MONTH_STRING_ISO = 'monthstringiso',
  MONTH_STRING_ISO_WO_CORRECTION = 'monthstringisowocorrection',
  DATE_MILIS = 'dateinmili',
  DATE_AM_PM = 'dateampm',
  YYYYMMDD_HH_MM_SS = 'datetime',
  DATE_ONLY = 'dateonly',
  DATE_LOCAL = 'datelocal',
  DATE_ONLY_WITHOUT_TIME = 'datewithouttime',
  DATE_ONLY_WITHOUT_TIME_REVERSE = 'datewithouttimereverse',
  YYYYMMDD_ONLY = 'YYYYMMDD_ONLY',
  DD_MMM_YYYY_HH_MM_AMPM = 'DD_MMM_YYYY_HH_MM_AMPM',
  YYYY_MM_dd_HHMM = 'yyyy-MM-dd hh:mm',
  YYYY_MM_dd_HHMMM = 'yyyy-MM-dd hh:mmm',
}

export const getRangeDate = (fromDate: Date, toDate?: Date): string => {
  if (toDate) {
    return `${dateFormatter(fromDate, DateFormatTypes.DAY_ONLY)} ${fromDate.getFullYear()} ${'-'} ${dateFormatter(
      toDate,
      DateFormatTypes.DAY_ONLY,
    )} ${toDate.getFullYear()}`
  }
  return `${dateFormatter(fromDate, DateFormatTypes.DAY_ONLY)} ${fromDate.getFullYear()}`
}

export const formatAMPM = (date: Date): string => {
  let hours = date.getHours()
  let minutes = date.getMinutes()
  let ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12
  hours = hours ? hours : 12
  const minutesString = minutes < 10 ? `0${minutes}` : `${minutes}`
  var strTime = hours + ':' + minutesString + ' ' + ampm
  return strTime
}

export const monthShotForm: Array<string> = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export const roundOfToTwoDigits2 = (value1: number): string => {
  const value = ++value1
  if (value < 10) {
    return `0${value}`
  }
  return value?.toString()
}

export const fromIsoDate = (isoDateString: string): Date => {
  const isoDate = new Date(isoDateString)
  return new Date(isoDate.getTime() + isoDate.getTimezoneOffset() * 60000)
}

const getMonthName: any = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec',
}

export const dateFormatter = (date: Date | string, type: DateFormatTypes, defaultVal = 'NA'): string => {
  console.log('defaultVal', defaultVal)
  if (defaultVal === '') {
    defaultVal = undefined
  }

  // //As per new date format expected in API's
  // if (date instanceof Date && type === DateFormatTypes.YYYYMMDD_HH_MM_SS) {
  //   return newDateFormat(date)
  // }

  if (typeof date === 'string' && type === DateFormatTypes.DATE_ONLY) {
    const dateObj: Date = date.includes(':') ? new Date(date) : new Date(+date)
    if (!dateObj.getDate()) {
      return defaultVal
    }
    return `${roundOfToTwoDigits(dateObj.getDate())}-${roundOfToTwoDigits2(
      dateObj.getMonth(),
    )}-${dateObj.getFullYear()}, ${formatAMPM(dateObj)}`
  }

  if (typeof date === 'string' && type === DateFormatTypes.DATE_ONLY_WITHOUT_TIME) {
    const dateObj: Date = date.includes(':') ? new Date(date) : new Date(+date)
    if (!dateObj.getDate()) {
      return defaultVal
    }
    return `${roundOfToTwoDigits(dateObj.getDate())}-${roundOfToTwoDigits2(
      dateObj.getMonth(),
    )}-${dateObj.getFullYear()}`
  }
  if (typeof date === 'string' && type === DateFormatTypes.DATE_ONLY_WITHOUT_TIME_REVERSE) {
    console.log('defaultVal 1', defaultVal)
    const dateObj: Date = date.includes(':') ? new Date(date) : new Date(+date)
    if (!dateObj.getDate()) {
      return defaultVal
    }
    return `${dateObj.getFullYear()}-${roundOfToTwoDigits2(dateObj.getMonth())}-${roundOfToTwoDigits(
      dateObj.getDate(),
    )}`
  }

  if (typeof date === 'string' && type === DateFormatTypes.MONTH_STRING) {
    const dateObj: Date = date.includes(':') ? new Date(date) : new Date(+date)
    if (!dateObj.getDate()) {
      return defaultVal
    }
    return `${dateObj.getDate()} ${monthShotForm[dateObj.getMonth()]} ${dateObj.getFullYear()}`
  }

  if (typeof date === 'string' && type === DateFormatTypes.MONTH_STRING_ISO) {
    const dateObj: Date = fromIsoDate(date)
    if (!dateObj.getDate()) {
      return 'NA'
    }
    return `${dateObj.getDate()} ${monthShotForm[dateObj.getMonth()]} ${dateObj.getFullYear()}`
  }
  if (typeof date === 'string' && type === DateFormatTypes.MONTH_STRING_ISO_WO_CORRECTION) {
    const dateObj: Date = new Date(date)
    if (!dateObj.getDate()) {
      return 'NA'
    }
    return `${dateObj.getDate()} ${monthShotForm[dateObj.getMonth()]} ${dateObj.getFullYear()}`
  }

  if (typeof date === 'string' && type === DateFormatTypes.DATE_AM_PM) {
    const dateObj: Date = date.includes(':') ? new Date(date) : new Date(+date)
    if (!dateObj.getDate()) {
      return 'NA'
    }
    return `${roundOfToTwoDigits(dateObj.getDate())}-${roundOfToTwoDigits2(
      dateObj.getMonth(),
    )}-${dateObj.getFullYear()}, ${formatAMPM(dateObj)}`
  }

  if (typeof date === 'string' && type === DateFormatTypes.DATE_LOCAL) {
    const formatedDate = date.includes(':') ? new Date(date).toLocaleString() : new Date(+date).toLocaleString()
    return `${
      formatedDate.slice(6, 10) +
      '-' +
      formatedDate.slice(3, 5) +
      '-' +
      formatedDate.slice(0, 2) +
      formatedDate.slice(11)
    }`
  }

  if (typeof date === 'string' && type === DateFormatTypes.DATE_MILIS) {
    const year = date.substring(0, 4)
    const month = date.substring(4, 6)
    const day = date.substring(6, 8)
    const hour = date.substring(8, 10)
    const minute = date.substring(10, 12)
    const second = date.substring(12, 14)
    const formatedDate = new Date(+year, +month - 1, +day, +hour, +minute, +second)
    return `${formatedDate.getTime()}`
  }

  if (typeof date === 'string' && type === DateFormatTypes.TIME_STAMP) {
    const dateObj: Date = date.includes(':') ? new Date(date) : new Date(+date)
    if (!dateObj.getDate()) {
      return 'NA'
    }
    const fDate = `${roundOfToTwoDigits(dateObj.getDate())}-${roundOfToTwoDigits2(
      dateObj.getMonth(),
    )}-${dateObj.getFullYear()} at ${roundOfToTwoDigits(dateObj.getHours())}:${roundOfToTwoDigits(
      dateObj.getMinutes(),
    )}:${roundOfToTwoDigits(dateObj.getSeconds())}`
    return fDate
  }

  if (typeof date === 'string' && date.length === 10 && type === DateFormatTypes.DD_MMM_YYYY) {
    const dateString = date?.substring(0, 2)
    const monthString = getMonthName[date?.substring(3, 5)]
    const year = date?.substring(6, 10)
    return `${dateString} ${monthString} ${year}`
  }

  if (date instanceof Date) {
    if (type === DateFormatTypes.YYYYMMDD) {
      return `${date.getFullYear()}-${roundOfToTwoDigits(date.getMonth() + 1)}-${roundOfToTwoDigits(date.getDate())} `
    }
    if (type === DateFormatTypes.DAY_ONLY) {
      return `${date.getDate()} ${monthShotForm[date.getMonth()]}`
    }
    if (type === DateFormatTypes.DATE_HYPHEN) {
      return `${date.getFullYear()}-${roundOfToTwoDigits(date.getMonth() + 1)}-${date.getDate()}`
    }
    if (type === DateFormatTypes.DATE_STRING) {
      return date.toLocaleDateString()
    }
    if (type === DateFormatTypes.YYYYMMDD_ONLY) {
      return `${date.getFullYear()}${roundOfToTwoDigits(date.getMonth() + 1)}${roundOfToTwoDigits(date.getDate())}`
    }
    if (type === DateFormatTypes.DD_MM_YYYY) {
      return `${roundOfToTwoDigits(date.getDate())}-${roundOfToTwoDigits(date.getMonth() + 1)}-${date.getFullYear()}`
    }
    if (type === DateFormatTypes.YYYY_MM_dd_HHMM) {
      return `${date.getFullYear()}-${roundOfToTwoDigits(date.getMonth() + 1)}-${roundOfToTwoDigits(
        date.getDate(),
      )} ${roundOfToTwoDigits(date.getHours())}:${roundOfToTwoDigits(date.getMinutes())}:00.000`
    }
    if (type === DateFormatTypes.YYYY_MM_dd_HHMMM) {
      return `${date.getFullYear()}-${roundOfToTwoDigits(date.getMonth() + 1)}-${roundOfToTwoDigits(
        date.getDate(),
      )} ${roundOfToTwoDigits(date.getHours())}:${roundOfToTwoDigits(date.getMinutes())}`
    }
  }

  if (typeof date === 'string' && type === DateFormatTypes.DD_MMM_YYYY_HH_MM_AMPM) {
    const year = date.substring(0, 4)
    const month = getMonthName[date.substring(4, 6)]
    const day = date.substring(6, 8)
    const hour = date.substring(8, 10)
    const minute = date.substring(10, 12)
    const ampm = +hour >= 12 ? 'PM' : 'AM'
    const timeWithoutMeridian = Number(hour) > 12 ? Number(hour) - 12 : hour
    return `${day} ${month} ${year}, ${returnFormattedTime(timeWithoutMeridian)}:${minute} ${ampm}`
  }
  return defaultVal
}
export const dateFormatterForDDMMYYYY = (date: string) => {
  const splitedDate = date.split('-')
  return `${splitedDate[2]}-${splitedDate[1]}-${splitedDate[0]}`
}

export const isMobile = (useIsLargeScreen: boolean) => {
  const isLarge = useIsLargeScreen

  if (Platform.OS === 'ios' || Platform.OS === 'android') {
    return true
  } else {
    if (!isLarge) {
      return true
    } else {
      return false
    }
  }
}

export const Colors = {
  PRIMARY: '#0186B0' as ColorOptions,
  PRIMARY_STORNG: '#027699',
  PRIMARY_LIGHT: '#F0F8FB',
  WHITE: '#ffffff',
  JIO_PRIMARY: '#214796' as ColorOptions,
  JIO_PRIMARY_ALPHA: 'rgb(74,104,169)' as ColorOptions,
  JIO_SECONDARY_ALPHA: 'rgb(119,142,190)' as ColorOptions,
  BLUE_LIGHT_ALPHA: 'rgb(58,83,152)' as ColorOptions,
  SECONDARY_GREY: '#E1E5E8' as ColorOptions,
  PRIMARY_GREY: '#888888' as ColorOptions,
  GREY_SEPARATOR: '#F0F0F0' as ColorOptions,
  BLUE_A9: '#4DBCA9',
  YELLOW_27: '#E89B27',
  BLUEFF: '#2797E8',
  VIOLETB7: '#663AB7',
  GREEN32: '#78B532',
  BLUE_FF: '#709DFF',
  BLUE_SHADE_PRIMARY: '#0070B6',
  BLACK_1A: '#7575751A',
  SEPARATOR: '#CCCCCC66',
  LIGHT_BLACK: '#7F7F7F',
  DISABLED_GREY: '#E0E0E0',
  GREEN_C: '#12802B',
  BLUE_C: '#1386B0',
  ORANGE_C: '#CC7F00',
  RED_C: '#CA4848',
  GREEN_BG_C: '#11C13A1A',
  BLUE_BG_C: '#1386B01A',
  ORANGE_BG_C: '#CC7F001A',
  RED_BG_C: '#CA48481A',
  GREY_BORDER: '#CCCCCC99',
  BRAND_PRIMARY_ACTION: '#1386B0',
  GREY_SHADE: '#CCCCCC',
  GREY_C: '#707070',
  LIGHT_GREY: '#0000001F',
  GRAY_SHADOW: '#00000029',
  DARK_GREY: '#555555',
  SPLASH: '#24397A',
  OTP_GREY: '#7F7F7F' as ColorOptions,
  HALF_WHITE: '#FAFAFA',
  DARK_BLUE: '#034EA8',
  TEAL: '#0186B01A',
  GREY_SHADE_1: '#BBBBBB',
  HELPER_TEXT: '#A0A0A0' as ColorOptions,
  MUD_YELLOW_DARK: '#99661F' as ColorOptions,
  WASHED_YELLOW: '#99661F50' as ColorOptions,
  WASHED_YELLOW_LIGHT: '#FEECA484' as ColorOptions,
  CITY_PINK: '#FF550021' as ColorOptions,
  ORANGE_4A: '#BE714A',
  AQUA_BLUE: '#F4F9FF',
  OCEAN_BLUE: '#4D89D3' as ColorOptions,
  BLACK_35: '#353535',
  GREY_90: '#909090',
  GREY_77: '#777777',
  BLACK_71: '#717171',
  BLACK_1D: '#1D1D1D',
  BLACK: '#000000' as ColorOptions,
  BLACK_LIGHT: '#00000050',
  ERROR: '#FC5A5A',
  BLUE_0A: '#0186B00A',
  BLACK_21: '#212121',
  BLACK_21_0D: '#2121210D', // 5% OF #212121
  GREY_E5: '#E5E5E5',
  LIGHT_GRAY: '#D0D0D0',
  GREY_EB: '#EBEBEB',
  BLUE_80: '#0186B080',
  TRANSPARENT: '#00000000',
  MODAL_SHADOW: 'rgba(0,0,0,0.6)',
  TEST_MODE: '#e68c32',
  LIVE_MODE: '#11c13a',
  CalendarColor: 'rgba(1,134,176,0.25)',
  BORDER_COLOR: '#00000014',
  LIGHT_BLUE: '#edf5fa',
  OVERLAY: 'rgba(255,255,255,0)',
  PRIMARY_GRADIENT: '#1F71A2',
  BASIC_WHITE: '#F0F5FA',
  SECONDARY_WHITE: '#FAFEFF',
  ERROR_RED: '#C94949',
  INFO_BORDER: '#E68E0052',
  INFO_BG: '#E68E0014',
}

export const getFutureDay = (date: Date, range: number): Date => {
  const nDate = new Date(date).getTime() + (range - 1) * 24 * 60 * 60 * 1000
  return new Date(nDate)
}

export const getPastDay = (date: Date, range: number): Date => {
  const nDate = new Date(date).getTime() - (range - 1) * 24 * 60 * 60 * 1000
  return new Date(nDate)
}

export const toLateNight = (date: Date): Date => {
  const midnightDate = new Date(date)
  midnightDate.setHours(23, 59, 59, 999)
  return midnightDate
}

export const setToDateTime = (secondDay: Date) => {
  const date: Date = new Date(Date.now())
  const todayDay = date.getDate()
  const todayMonth = date.getMonth()
  const todayYear = date.getFullYear()
  const isTodaySelected = () => {
    if (
      todayDay === secondDay.getDate() &&
      todayMonth === secondDay.getMonth() &&
      todayYear === secondDay.getFullYear()
    ) {
      return true
    } else {
      return false
    }
  }

  if (isTodaySelected()) {
    return date
  } else {
    return toLateNight(secondDay)
  }
}

export const getGenderFromSalutation = (salutation: string) => {
  const gender: any = {
    mrs: 'Female',
    ms: 'Female',
    mr: 'Male',
  }
  return gender[salutation?.toLowerCase()]
}

export const jsonToCsvFileDownload = (jsonData: any, fileName: string) => {
  const arrData = typeof jsonData !== 'object' ? JSON.parse(jsonData) : jsonData
  let csv = ''

  for (let i = 0; i < arrData.length; i++) {
    let row = ''
    for (const index in arrData[i]) {
      row += '"' + arrData[i][index] + '",'
    }
    row.slice(0, row.length - 1)
    csv += row + '\r\n'
  }

  if (csv === '') {
    alert('Invalid data')
    return
  }

  fileName += fileName.replace(/ /g, '_')

  const uri = 'data:text/csv;charset=utf-8,' + escape(csv)

  const link = document.createElement('a')
  link.href = uri

  link.style = 'visibility:hidden'
  link.download = fileName + '.csv'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const convertToCSV = (objArray: any) => {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray
  let str = ''
  for (let i = 0; i < array.length; i++) {
    let line = ''
    for (const index in array[i]) {
      if (line != '') {
        line += ','
      }
      line += array[i][index]
    }
    str += line + '\r\n'
  }
  return str
}

export const exportCSVFile = (headers: any, items: any, fileTitle: string) => {
  if (headers) {
    items.unshift(headers)
  }
  const jsonObject = JSON.stringify(items)
  const csv = convertToCSV(jsonObject)
  const exportedFilenmae = fileTitle + '.csv' || 'export.csv'
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  if (link.download !== undefined) {
    var url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', exportedFilenmae)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}
export const sampleHeaders = [
  'EMI_BANK_IDs',
  'BANK_NAME',
  'TENURE_ID',
  'SCHEME_ID',
  'SCHEME_SUBTYPE',
  'MIN_AMOUNT',
  'MAX_AMOUNT',
  'INTEREST_RATE',
  'REMARKS',
]

export const sampleDataToExportCsv = [
  {
    EMI_BANK_ID: '2',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '1',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'HDFC CC 3EMI',
  },
  {
    EMI_BANK_ID: '1',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '2',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'HDFC CC 6EMI',
  },
  {
    EMI_BANK_ID: '1',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '3',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'HDFC CC 9EMI ',
  },
  {
    EMI_BANK_ID: '1',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '4',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'HDFC DC 3EMI',
  },
  {
    EMI_BANK_ID: '1',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '5',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'HDFC DC 6EMI',
  },
  {
    EMI_BANK_ID: '1',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '8',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'HDFC CC 12EMI',
  },
  {
    EMI_BANK_ID: '1',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '18M',
    SCHEME_ID: '9',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'HDFC CC 18EMI',
  },
  {
    EMI_BANK_ID: '1',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '24M',
    SCHEME_ID: '10',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'HDFC CC 24EMI ',
  },
  {
    EMI_BANK_ID: '1',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '95',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'HDFC DC 9EMI',
  },
  {
    EMI_BANK_ID: '1',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '96',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'HDFC DC 12EMI',
  },
  {
    EMI_BANK_ID: '1',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '18M',
    SCHEME_ID: '97',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'HDFC DC 18EMI',
  },
  {
    EMI_BANK_ID: '1',
    BANK_NAME: 'HDFC Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '118',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'HDFC CC 3EMI NOCOST',
  },
  {
    EMI_BANK_ID: '10',
    BANK_NAME: 'HSBC Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '47',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'HSBC CC 3EMI',
  },
  {
    EMI_BANK_ID: '10',
    BANK_NAME: 'HSBC Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '48',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'HSBC CC 6EMI',
  },
  {
    EMI_BANK_ID: '10',
    BANK_NAME: 'HSBC Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '49',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'HSBC CC 9EMI',
  },
  {
    EMI_BANK_ID: '10',
    BANK_NAME: 'HSBC Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '50',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'HSBC CC 12EMI',
  },
  {
    EMI_BANK_ID: '10',
    BANK_NAME: 'HSBC Bank',
    TENURE_ID: '18M',
    SCHEME_ID: '51',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'HSBC CC 18EMI',
  },
  {
    EMI_BANK_ID: '10',
    BANK_NAME: 'HSBC Bank',
    TENURE_ID: '24M',
    SCHEME_ID: '52',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'HSBC CC 24EMI',
  },
  {
    EMI_BANK_ID: '11',
    BANK_NAME: 'RBL Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '53',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'RBL CC 3EMI',
  },
  {
    EMI_BANK_ID: '11',
    BANK_NAME: 'RBL Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '54',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'RBL CC 6EMI',
  },
  {
    EMI_BANK_ID: '11',
    BANK_NAME: 'RBL Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '55',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'RBL CC 9EMI',
  },
  {
    EMI_BANK_ID: '11',
    BANK_NAME: 'RBL Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '56',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'RBL CC 12EMI',
  },
  {
    EMI_BANK_ID: '11',
    BANK_NAME: 'RBL Bank',
    TENURE_ID: '18M',
    SCHEME_ID: '57',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'RBL CC 18EMI',
  },
  {
    EMI_BANK_ID: '11',
    BANK_NAME: 'RBL Bank',
    TENURE_ID: '24M',
    SCHEME_ID: '58',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'RBL CC 24EMI',
  },
  {
    EMI_BANK_ID: '12',
    BANK_NAME: 'IndusInd Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '59',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'INDUS CC 3EMI',
  },
  {
    EMI_BANK_ID: '12',
    BANK_NAME: 'IndusInd Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '60',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'INDUS CC 6EMI',
  },
  {
    EMI_BANK_ID: '12',
    BANK_NAME: 'IndusInd Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '61',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'INDUS CC 9EMI',
  },
  {
    EMI_BANK_ID: '12',
    BANK_NAME: 'IndusInd Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '62',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '12',
    REMARKS: 'INDUS CC 12EMI',
  },
  {
    EMI_BANK_ID: '12',
    BANK_NAME: 'IndusInd Bank',
    TENURE_ID: '18M',
    SCHEME_ID: '63',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '12',
    REMARKS: 'INDUS CC 18EMI',
  },
  {
    EMI_BANK_ID: '12',
    BANK_NAME: 'IndusInd Bank',
    TENURE_ID: '24M',
    SCHEME_ID: '64',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '12',
    REMARKS: 'INDUS CC 24EMI',
  },
  {
    EMI_BANK_ID: '13',
    BANK_NAME: 'Yes Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '65',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'YES CC 3EMI',
  },
  {
    EMI_BANK_ID: '13',
    BANK_NAME: 'Yes Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '66',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'YES CC 6EMI',
  },
  {
    EMI_BANK_ID: '13',
    BANK_NAME: 'Yes Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '67',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'YES CC 9EMI',
  },
  {
    EMI_BANK_ID: '13',
    BANK_NAME: 'Yes Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '68',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'YES CC 12EMI',
  },
  {
    EMI_BANK_ID: '13',
    BANK_NAME: 'Yes Bank',
    TENURE_ID: '18M',
    SCHEME_ID: '69',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'YES CC 18EMI',
  },
  {
    EMI_BANK_ID: '13',
    BANK_NAME: 'Yes Bank',
    TENURE_ID: '24M',
    SCHEME_ID: '70',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'YES CC 24EMI',
  },
  {
    EMI_BANK_ID: '14',
    BANK_NAME: 'American Express',
    TENURE_ID: '3M',
    SCHEME_ID: '71',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'AMEX CC 3EMI',
  },
  {
    EMI_BANK_ID: '14',
    BANK_NAME: 'American Express',
    TENURE_ID: '6M',
    SCHEME_ID: '72',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'AMEX CC 6EMI',
  },
  {
    EMI_BANK_ID: '14',
    BANK_NAME: 'American Express',
    TENURE_ID: '9M',
    SCHEME_ID: '73',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'AMEX CC 9EMI',
  },
  {
    EMI_BANK_ID: '14',
    BANK_NAME: 'American Express',
    TENURE_ID: '12M',
    SCHEME_ID: '74',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'AMEX CC 12EMI',
  },
  {
    EMI_BANK_ID: '14',
    BANK_NAME: 'American Express',
    TENURE_ID: '18M',
    SCHEME_ID: '75',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'AMEX CC 18EMI',
  },
  {
    EMI_BANK_ID: '14',
    BANK_NAME: 'American Express',
    TENURE_ID: '24M',
    SCHEME_ID: '76',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'AMEX CC 24EMI',
  },
  {
    EMI_BANK_ID: '15',
    BANK_NAME: 'Jammu and Kashmir Bank Limited',
    TENURE_ID: '3M',
    SCHEME_ID: '77',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '12',
    REMARKS: 'J and K CC 3EMI',
  },
  {
    EMI_BANK_ID: '15',
    BANK_NAME: 'Jammu and Kashmir Bank Limited',
    TENURE_ID: '6M',
    SCHEME_ID: '78',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '12',
    REMARKS: 'J and K CC 6EMI',
  },
  {
    EMI_BANK_ID: '15',
    BANK_NAME: 'Jammu and Kashmir Bank Limited',
    TENURE_ID: '9M',
    SCHEME_ID: '79',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'J and K CC 9EMI',
  },
  {
    EMI_BANK_ID: '15',
    BANK_NAME: 'Jammu and Kashmir Bank Limited',
    TENURE_ID: '12M',
    SCHEME_ID: '80',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'J and K CC 12EMI',
  },
  {
    EMI_BANK_ID: '15',
    BANK_NAME: 'Jammu and Kashmir Bank Limited',
    TENURE_ID: '18M',
    SCHEME_ID: '81',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'J and K CC 18EMI',
  },
  {
    EMI_BANK_ID: '15',
    BANK_NAME: 'Jammu and Kashmir Bank Limited',
    TENURE_ID: '24M',
    SCHEME_ID: '82',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'J and K CC 24EMI',
  },
  {
    EMI_BANK_ID: '16',
    BANK_NAME: 'Bank of Baroda',
    TENURE_ID: '3M',
    SCHEME_ID: '83',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'BOB CC 3EMI',
  },
  {
    EMI_BANK_ID: '16',
    BANK_NAME: 'Bank of Baroda',
    TENURE_ID: '6M',
    SCHEME_ID: '84',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'BOB CC 6EMI',
  },
  {
    EMI_BANK_ID: '16',
    BANK_NAME: 'Bank of Baroda',
    TENURE_ID: '9M',
    SCHEME_ID: '85',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'BOB CC 9EMI',
  },
  {
    EMI_BANK_ID: '16',
    BANK_NAME: 'Bank of Baroda',
    TENURE_ID: '12M',
    SCHEME_ID: '86',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'BOB CC 12EMI',
  },
  {
    EMI_BANK_ID: '16',
    BANK_NAME: 'Bank of Baroda',
    TENURE_ID: '18M',
    SCHEME_ID: '87',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'BOB CC 18EMI',
  },
  {
    EMI_BANK_ID: '16',
    BANK_NAME: 'Bank of Baroda',
    TENURE_ID: '24M',
    SCHEME_ID: '88',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'BOB CC 24EMI',
  },
  {
    EMI_BANK_ID: '18',
    BANK_NAME: 'ICICI Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '6',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'ICICI DC 3EMI',
  },
  {
    EMI_BANK_ID: '18',
    BANK_NAME: 'ICICI Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '7',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'ICICI DC 6EMI',
  },
  {
    EMI_BANK_ID: '18',
    BANK_NAME: 'ICICI Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '17',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'ICICI CC 3EMI',
  },
  {
    EMI_BANK_ID: '18',
    BANK_NAME: 'ICICI Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '18',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'ICICI CC 6EMI',
  },
  {
    EMI_BANK_ID: '18',
    BANK_NAME: 'ICICI Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '19',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'ICICI CC 9EMI',
  },
  {
    EMI_BANK_ID: '18',
    BANK_NAME: 'ICICI Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '20',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'ICICI CC 12EMI',
  },
  {
    EMI_BANK_ID: '18',
    BANK_NAME: 'ICICI Bank',
    TENURE_ID: '18M',
    SCHEME_ID: '21',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'ICICI CC 18EMI',
  },
  {
    EMI_BANK_ID: '18',
    BANK_NAME: 'ICICI Bank',
    TENURE_ID: '24M',
    SCHEME_ID: '22',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '1500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'ICICI CC 24EMI',
  },
  {
    EMI_BANK_ID: '18',
    BANK_NAME: 'ICICI Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '98',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'ICICI DC 8EMI',
  },
  {
    EMI_BANK_ID: '18',
    BANK_NAME: 'ICICI Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '99',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'ICICI DC 12EMI',
  },
  {
    EMI_BANK_ID: '19',
    BANK_NAME: 'Federal Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '100',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14.5',
    REMARKS: 'Federal Bank DC 3EMI',
  },
  {
    EMI_BANK_ID: '19',
    BANK_NAME: 'Federal Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '101',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14.5',
    REMARKS: 'Federal Bank DC 6EMI',
  },
  {
    EMI_BANK_ID: '19',
    BANK_NAME: 'Federal Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '102',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14.5',
    REMARKS: 'Federal Bank DC 9EMI',
  },
  {
    EMI_BANK_ID: '19',
    BANK_NAME: 'Federal Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '103',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14.5',
    REMARKS: 'Federal Bank DC 12EMI',
  },
  {
    EMI_BANK_ID: '5',
    BANK_NAME: 'Citibank',
    TENURE_ID: '3M',
    SCHEME_ID: '11',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'CITI CC 3EMI',
  },
  {
    EMI_BANK_ID: '5',
    BANK_NAME: 'Citibank',
    TENURE_ID: '6M',
    SCHEME_ID: '12',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'CITI CC 6EMI',
  },
  {
    EMI_BANK_ID: '5',
    BANK_NAME: 'Citibank',
    TENURE_ID: '9M',
    SCHEME_ID: '13',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'CITI CC 9EMI',
  },
  {
    EMI_BANK_ID: '5',
    BANK_NAME: 'Citibank',
    TENURE_ID: '12M',
    SCHEME_ID: '14',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'CITI CC 12EMI',
  },
  {
    EMI_BANK_ID: '5',
    BANK_NAME: 'Citibank',
    TENURE_ID: '18M',
    SCHEME_ID: '15',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'CITI CC 18EMI',
  },
  {
    EMI_BANK_ID: '5',
    BANK_NAME: 'Citibank',
    TENURE_ID: '24M',
    SCHEME_ID: '16',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'CITI CC 24EMI',
  },
  {
    EMI_BANK_ID: '6',
    BANK_NAME: 'State Bank of India',
    TENURE_ID: '3M',
    SCHEME_ID: '23',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'SBI CC 3EMI',
  },
  {
    EMI_BANK_ID: '6',
    BANK_NAME: 'State Bank of India',
    TENURE_ID: '6M',
    SCHEME_ID: '24',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'SBI CC 6EMI',
  },
  {
    EMI_BANK_ID: '6',
    BANK_NAME: 'State Bank of India',
    TENURE_ID: '9M',
    SCHEME_ID: '25',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'SBI CC 9EMI',
  },
  {
    EMI_BANK_ID: '6',
    BANK_NAME: 'State Bank of India',
    TENURE_ID: '12M',
    SCHEME_ID: '26',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'SBI CC 12EMI',
  },
  {
    EMI_BANK_ID: '6',
    BANK_NAME: 'State Bank of India',
    TENURE_ID: '18M',
    SCHEME_ID: '27',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'SBI CC 18EMI',
  },
  {
    EMI_BANK_ID: '6',
    BANK_NAME: 'State Bank of India',
    TENURE_ID: '24M',
    SCHEME_ID: '28',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'SBI CC 24EMI',
  },
  {
    EMI_BANK_ID: '7',
    BANK_NAME: 'Kotak Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '29',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '12',
    REMARKS: 'KOTAK CC 3EMI',
  },
  {
    EMI_BANK_ID: '7',
    BANK_NAME: 'Kotak Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '30',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '12',
    REMARKS: 'KOTAK CC 6EMI',
  },
  {
    EMI_BANK_ID: '7',
    BANK_NAME: 'Kotak Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '31',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'KOTAK CC 9EMI',
  },
  {
    EMI_BANK_ID: '7',
    BANK_NAME: 'Kotak Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '32',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'KOTAK CC 12EMI',
  },
  {
    EMI_BANK_ID: '7',
    BANK_NAME: 'Kotak Bank',
    TENURE_ID: '18M',
    SCHEME_ID: '33',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'KOTAK CC 18EMI',
  },
  {
    EMI_BANK_ID: '7',
    BANK_NAME: 'Kotak Bank',
    TENURE_ID: '24M',
    SCHEME_ID: '34',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '3000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'KOTAK CC 24EMI',
  },
  {
    EMI_BANK_ID: '8',
    BANK_NAME: 'Standard Chartered Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '35',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '11.88',
    REMARKS: 'SCB CC 3EMI',
  },
  {
    EMI_BANK_ID: '8',
    BANK_NAME: 'Standard Chartered Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '36',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'SCB CC 6EMI',
  },
  {
    EMI_BANK_ID: '8',
    BANK_NAME: 'Standard Chartered Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '37',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'SCB CC 9EMI',
  },
  {
    EMI_BANK_ID: '8',
    BANK_NAME: 'Standard Chartered Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '38',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'SCB CC 12EMI',
  },
  {
    EMI_BANK_ID: '8',
    BANK_NAME: 'Standard Chartered Bank',
    TENURE_ID: '18M',
    SCHEME_ID: '39',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'SCB CC 18EMI',
  },
  {
    EMI_BANK_ID: '8',
    BANK_NAME: 'Standard Chartered Bank',
    TENURE_ID: '24M',
    SCHEME_ID: '40',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'SCB CC 24EMI',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '41',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'AXIS CC 3EMI',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '42',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '13',
    REMARKS: 'AXIS CC 6EMI',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '43',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'AXIS CC 9EMI',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '44',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'AXIS CC 12EMI',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '18M',
    SCHEME_ID: '45',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'AXIS CC 18EMI',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '24M',
    SCHEME_ID: '46',
    SCHEME_SUBTYPE: '00',
    MIN_AMOUNT: '2500',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '15',
    REMARKS: 'AXIS CC 24EMI',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '3M',
    SCHEME_ID: '119',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'Axis Bank DC EMI 3M',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '6M',
    SCHEME_ID: '120',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '14',
    REMARKS: 'Axis Bank DC EMI 6M',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '9M',
    SCHEME_ID: '121',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'Axis Bank DC EMI 9M',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '12M',
    SCHEME_ID: '122',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'Axis Bank DC EMI 12M',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '18M',
    SCHEME_ID: '123',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'Axis Bank DC EMI 18M',
  },
  {
    EMI_BANK_ID: '9',
    BANK_NAME: 'Axis Bank',
    TENURE_ID: '24M',
    SCHEME_ID: '124',
    SCHEME_SUBTYPE: '01',
    MIN_AMOUNT: '5000',
    MAX_AMOUNT: '999999999999999',
    INTEREST_RATE: '16',
    REMARKS: 'Axis Bank DC EMI 24M',
  },
]

export const DOBValidation = Yup.string()
  .required('Required')
  .matches(/^([0-2][0-9]|(3)[0-1])(-)(((0)[0-9])|((1)[0-2]))(-)\d{4}$/, 'Invalid Date')

export const DobNotMandateValidation = Yup.string().matches(
  /^([0-2][0-9]|(3)[0-1])(-)(((0)[0-9])|((1)[0-2]))(-)\d{4}$/,
  'Invalid Date',
)

export const allowedTextValidation = Yup.string()
  .required('Required')
  .matches(/^[A-Za-z0-9? \/&-_.]+$/, 'Invalid value')

export const panValidation = Yup.string()
  .trim()
  .matches(/[A-Za-z]{3}[ABCFGHLJPTFabcfghljptf]{1}[A-Za-z]{1}[0-9]{4}[A-Za-z]{1}/, 'Incorrect Pan Number')
  .required()

export const webUrl = Yup.string()
  .matches(
    /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm,
    'Enter correct web address',
  )
  .required('Please enter web address')

export const gstValidation = Yup.string()
  .trim()
  .required()
  .matches(/\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/, 'Invalid GST Number')

export const pincodeNotMandateValidation = Yup.string()
  .notRequired()
  .max(6, 'Please enter 6 digit pincode')
  .matches(/^(\s*|\d{6}|NA)$/, 'Incorrect pincode')

export const getHHMMSS = (date: Date) => {
  return (
    ('0' + date.getHours()).slice(-2) +
    ':' +
    ('0' + date.getMinutes()).slice(-2) +
    ':' +
    ('0' + date.getSeconds()).slice(-2)
  )
}

export const fromDateTime = (fromDate: Date): string => {
  const startTime = getHHMMSS(fromDate)
  return `${dateFormatter(fromDate, DateFormatTypes.YYYYMMDD)}${startTime}`
}

export const toDateTime = (toDate: Date): string => {
  const today = new Date(Date.now())
  /* check if end date in past , so end time will be 23:59:59 ,
    if its today so time will be current time */
  const endTime = toDate.toDateString() === today.toDateString() ? getHHMMSS(today) : '23:59:59'
  return `${dateFormatter(toDate, DateFormatTypes.YYYYMMDD)}${endTime}`
}

export const getYesterday = (type = 'END'): Date => {
  const day = new Date(new Date(new Date().setDate(new Date().getDate() - 1)))
  if (type === 'START') {
    day.setHours(0, 0, 0, 0)
  } else if ('END') {
    day.setHours(23, 59, 59, 999)
  }
  return day
}

export const toIsoDate = (date: Date): string => {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString()
}

export const parseDateWithTime = (date: Date): string => {
  try {
    return `${padZero(date.getDate())}-${padZero(date.getMonth() + 1)}-${date.getFullYear()} ${padZero(
      date.getHours(),
    )}:${padZero(date.getMinutes())}`
  } catch (error) {
    return ''
  }
}
export const filtersData = {
  data: ['ALL', 'Completed', 'Rejected', 'Approved', 'Pending For Approval', 'Error'],
  title: 'Application Status',
  mode: 'applicationStatus',
}

export const filtersAuditData = {
  data: ['ALL', 'DIRECTOR', 'AUTHORISED_SIGNATORY', 'CIN', 'UBO'],
  title: 'Audit Type',
  mode: 'auditType',
}

export const alphaNumericNotMandatoryValidation = Yup.string()
  .matches(/^[a-zA-Z0-9]*$/, 'Only alpha numeric accepted')
  .notRequired()
  .nullable()
  .strict(false)

export const methodTypes = {
  112: 'DEBIT ADJUSTMENT',
  214: 'CREDIT ADJUSTMENT',
  217: 'REFUND LIEN_MARKING',
  311: 'REFUND LIEN RELEASE',
  110: 'PURCHASE',
  111: 'DEBIT',
  211: 'REFUND',
  212: 'MERCHANT CREDIT',
  213: 'CREDIT',
  371: 'TRANSACTION CLOSURE',
  470: 'UPI P2P',
  471: 'UPI P2M',
  420: 'IMPS',
  304: 'ACCVERIFY',
  300: 'INTENT',
  310: 'AUTOPAY',
  306: 'PRE-DEBIT NOTIFICATION',
  114: 'EMI PURCHASE',
  302: 'REVERSAL',
}

export const methodSubTypes = {
  525: 'CASH',
  560: 'UPI PAY',
  561: 'UPI COLLECTION',
  562: 'UPI ACCEPT REJECT',
  563: 'UPI BILL PAY',
  564: 'UPI_MERCHANT_PAY',
  565: 'UPI BILL PAY COLLECT',
  566: 'UPI MERCHANT PAY COLLECT',
  553: 'BANK',
  576: 'MANDATE',
  575: 'WEBPAY',
  550: 'P2A',
  580: 'UPI PAY',
  581: 'NET BANKING',
  620: 'CORP NET BANKING',
  582: 'CREDIT CARD',
  583: 'MANDATE PAYER CREATE',
  579: 'WALLET',
  589: 'UPI',
  590: 'UPI',
  591: 'SI CARD DEMAND',
  592: 'DC CARD EMI',
  599: 'SI CARD CREATE MANDATE',
  600: 'CARDLESS',
  612: 'DC CARDLESS',
  601: 'PAYLATER',
  598: 'POS PAYMENT',
  613: 'NON-PCI FOOD',
  616: 'PAYLATER_V2',
  626: 'LOAN DISBURSEMENT',
  625: 'ADJUSTMENT WITHOUT ORIGINAL VALIDATION',
  624: 'ADJUSTMENT WITH ORIGINAL VALIDATION',
  605: 'REFUND',
}
export const formatDateTime = (date: Date) => {
  return new Date(date.getTime() - new Date().getTimezoneOffset() * 60000).toISOString()
}

export const getAcceptableStatus = (statusArr: any[]) => {
  return statusArr?.includes('Pending For Approval')
    ? statusArr?.map((status) => (status === 'Pending For Approval' ? 'Pending' : status))
    : statusArr
}

export const waitInterval = (time: number) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve(undefined)
    }, time)
  })

export const encryptAccNumber = (accNUmber: string) => {
  const encrypt = new JSEncrypt()
  encrypt.setPublicKey(PUBLIC_ENC_KEY_ACC_MUMBER)
  return encrypt.encrypt(accNUmber)
}

export const getStartDate = (days: number) => {
  const today = new Date(Date.now())
  const date = new Date(today.setDate(today.getDate() - days))
  const finalDate = new Date(date.setSeconds(date.getSeconds() + 1))
  const startTime = getHHMMSS(finalDate)
  return `${dateFormatter(date, DateFormatTypes.YYYYMMDD)}${startTime}`
}

export const getCurrentDate = () => {
  const today = new Date(Date.now())
  const startTime = getHHMMSS(today)
  return `${dateFormatter(today, DateFormatTypes.YYYYMMDD)}${startTime}`
}

export const riskHoldStatusMap = {
  PAYOUT_STAGED: 'RISK_HOLD',
  PAYOUT_SPLIT_STAGED: 'RISK_HOLD',
  RISK_HOLD: 'PAYOUT_STAGED',
  HOLD: 'PAYOUT_STAGED',
  PAYOUT_RISK_HOLD_SPLIT: 'PAYOUT_SPLIT_STAGED',
  PAYOUT_HOLD_SPLIT: 'PAYOUT_SPLIT_STAGED',
  LIMIT_HOLD: 'READY',
  DMS_HOLD: 'READY',
  // PAYOUT_STAGED: 'HOLD',
  // PAYOUT_SPLIT_STAGED: 'HOLD'
}

export const statusActionMap = {
  PAYOUT_STAGED: 'Hold',
  PAYOUT_SPLIT_STAGED: 'Hold',
  RISK_HOLD: 'Release',
  HOLD: 'Release',
  PAYOUT_RISK_HOLD_SPLIT: 'Release',
  PAYOUT_HOLD_SPLIT: 'Release',
  LIMIT_HOLD: 'Release',
  DMS_HOLD: 'Release',
}

export const riskUpdateMap = {
  LIMIT_HOLD: 'STATUS_UPDATE_MDR_LIMIT_RELEASE',
  RISK_HOLD: 'STATUS_UPDATE_RISK_HOLD_RELEASE',
  DMS_HOLD: 'STATUS_UPDATE_MDR_DMS_RELEASE',
  HOLD: 'STATUS_UPDATE_HOLD_RELEASE',
  PAYOUT_SPLIT_STAGED: 'STATUS_UPDATE_SPLIT_RISK_HOLD',
  PAYOUT_RISK_HOLD_SPLIT: 'STATUS_UPDATE_SPLIT_RISK_HOLD_RELEASE',
  PAYOUT_HOLD_SPLIT: 'STATUS_UPDATE_SPLIT_HOLD_RELEASE',
  PAYOUT_STAGED: 'STATUS_UPDATE_RISK_HOLD',
}

export const riskControlsStatus = [
  { id: '1', value: 'RISK_HOLD' },
  { id: '2', value: 'LIMIT_HOLD' },
  { id: '3', value: 'PAYOUT_RISK_HOLD_SPLIT' },
]

export const allowedTextValidationNotRequired = Yup.string().matches(/^[A-Za-z0-9? \/&-_.]*$/, 'Invalid value')

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms))

export const getIncreasingDelay = (retry: number) => Math.min(retry * 1000, 5000)

export function isNumeric(str) {
  if (typeof str !== 'string') {
    return false
  } // We only process strings!
  return !isNaN(Number(str))
}

export function excelSerialToDate(serial) {
  // Excel considers 1900 as a leap year, so we need to adjust the serial number
  const excelEpochStart = new Date(1899, 11, 30) // This represents 1899-12-30

  // Add the serial number to the epoch start date
  const resultDate = new Date(excelEpochStart.getTime() + serial * 86400000) // 86400000 ms in a day

  return formatDateToDDMMYYYY(resultDate)
}

function formatDateToDDMMYYYY(date) {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are 0-based
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

export function isValidDateFormat(dateStr) {
  // Regular expression to match the DD/MM/YYYY format
  const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/

  // Test the string against the regex
  if (!regex.test(dateStr)) {
    return false
  }

  // Additional validation to check if the date is actually valid
  const parts = dateStr.split('/')
  const day = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10)
  const year = parseInt(parts[2], 10)

  // Check if the month is valid
  if (month < 1 || month > 12) {
    return false
  }

  // Days in each month
  const monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

  // Check for leap year
  if (month === 2 && ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0)) {
    monthDays[1] = 29
  }

  // Check if the day is valid for the given month
  if (day < 1 || day > monthDays[month - 1]) {
    return false
  }

  return true
}

export const getValueFromLOVDescription = (lovData: any, description: string, key: string) => {
  return lovData?.find((item) => item.description.trim() === description?.trim() && item.key === key)?.value
}

export const getDescriptionFromLOVValue = (lovData: any, key: string, value: string, defaultVal = 'NA') => {
  return lovData?.find((item) => item.value === value && item.key === key)?.description ?? defaultVal
}

export const compareArray = (array1, array2) =>
  array1.length === array2.length && array1.every((value, index) => value === array2[index])

export const LOV_CODES = {
  ONLINE: '1',
  PHYSICAL: '2',
  SOLE_TRADER: '100',
  PARTNERSHIP: '101',
  INDIVIDUAL: '107',
}

export const zoomOutIfRequire = (nativeID: string) => {
  if (Dimensions.get('window').height < MIN_HEIGHT_FOR_NO_ZOOM) {
    const element = document.getElementById(nativeID)
    element.style.transform = `scale(${ZOOM_FRACTION})`
    element.style.transformOrigin = 'top left'
    element.style.height = `${(1 / ZOOM_FRACTION) * 100}%`
    element.style.width = `${(1 / ZOOM_FRACTION) * 100}%`
  }
}
